/**
 * Types du domaine client.
 *
 * `ClientSummary` et `clientDisplayName` vivent dans le domaine devis, où ils
 * ont été introduits en premier. Ils sont réexportés ici : un client reste un
 * client, quel que soit le document qui le mentionne.
 */
import { round2 } from "../../money.js";
import type { InvoiceStatus } from "../invoices/types.js";
import { isSettledStatus } from "../invoices/types.js";

export type { ClientSummary } from "../quotes/types.js";
export { clientDisplayName } from "../quotes/types.js";

/**
 * Un particulier se désigne par son nom, les autres par leur raison sociale.
 * Une collectivité est toujours une entité publique : c'est ce qui la rend
 * assujettie à Chorus Pro, donc ça se vérifie plutôt que ça ne se coche.
 */
export type ClientType = "particulier" | "professionnel" | "syndic" | "collectivite";

export interface Client {
  id: string;
  type: ClientType;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  siret: string | null;
  email: string | null;
  phone: string | null;
  phoneSecondary: string | null;
  addressLine1: string | null;
  postalCode: string | null;
  city: string | null;
  notes: string | null;
  isPublicEntity: boolean;
  chorusProStructureId: string | null;
  createdAt: string;
}

/** Documents rattachés, tels qu'affichés sur la fiche. */
export interface ClientQuoteRef {
  id: string;
  quoteNumber: string;
  title: string;
  totalTtc: number;
  status: string;
  createdAt: string;
}

export interface ClientInvoiceRef {
  id: string;
  invoiceNumber: string;
  title: string;
  totalTtc: number;
  amountPaid: number;
  status: InvoiceStatus;
  dueDate: string;
}

export interface ClientProjectRef {
  id: string;
  name: string;
  status: string;
  plannedStartDate: string | null;
}

export interface ClientDetail {
  client: Client;
  quotes: ClientQuoteRef[];
  invoices: ClientInvoiceRef[];
  projects: ClientProjectRef[];
  /** Ce que ce client doit encore. Voir `computeOutstanding`. */
  outstanding: Outstanding;
}

export interface Outstanding {
  /** Facturé et non encaissé, échéance non dépassée comprise. */
  totalDue: number;
  /** La part dont l'échéance est passée — celle qui coûte de la trésorerie. */
  overdueDue: number;
  overdueCount: number;
  /**
   * La part portée par des factures encore en brouillon.
   *
   * Mêmes champs et même raisonnement que `Receivables` côté facturation :
   * c'est le même encours vu à travers un seul client, et deux définitions du
   * mot « dû » finiraient par ne plus donner le même total.
   */
  unsentDue: number;
  unsentCount: number;
  /** La part de `overdueDue` qui vient de brouillons. */
  unsentOverdueDue: number;
}

/**
 * Encours d'un client.
 *
 * Le patron veut une réponse à « qui me doit de l'argent ? », et surtout
 * « depuis quand ». On distingue donc le dû du dû en retard : le premier est
 * normal, le second est le problème.
 *
 * On raisonne sur `totalTtc - amountPaid` plutôt que sur le statut seul : une
 * facture partiellement réglée reste due pour son solde, et son statut ne le
 * dit pas.
 */
export function computeOutstanding(
  invoices: readonly ClientInvoiceRef[],
  today: Date = new Date(),
): Outstanding {
  const todayIso = today.toISOString().slice(0, 10);
  let totalDue = 0;
  let overdueDue = 0;
  let overdueCount = 0;
  let unsentDue = 0;
  let unsentCount = 0;
  let unsentOverdueDue = 0;

  for (const invoice of invoices) {
    if (isSettledStatus(invoice.status)) continue;

    const remaining = invoice.totalTtc - invoice.amountPaid;
    if (remaining <= 0) continue;

    totalDue += remaining;

    // Une facture jamais envoyée est due sur le papier, mais rien ne la
    // réclamera : ni le cron d'échéance ni la cadence de relance ne voient un
    // brouillon. La compter sans le dire ferait passer pour un client mauvais
    // payeur quelqu'un à qui l'on n'a rien demandé.
    const jamaisEnvoyee = invoice.status === "draft";
    if (jamaisEnvoyee) {
      unsentDue += remaining;
      unsentCount += 1;
    }

    if (invoice.dueDate < todayIso) {
      overdueDue += remaining;
      overdueCount += 1;
      if (jamaisEnvoyee) unsentOverdueDue += remaining;
    }
  }

  return {
    totalDue: round2(totalDue),
    overdueDue: round2(overdueDue),
    overdueCount,
    unsentDue: round2(unsentDue),
    unsentCount,
    unsentOverdueDue: round2(unsentOverdueDue),
  };
}
